import { useQuery } from '@tanstack/vue-query'
import { computed, reactive } from 'vue'

declare global {
	interface Window {
		__enlaceGoogleMapsReady?: () => void
	}
}

export interface GeocodeResult {
	address_components: AddressComponent[]
	formatted_address: string
	geometry: {
		location: {
			lat: number
			lng: number
		}
		location_type: string
		viewport: {
			northeast: {
				lat: number
				lng: number
			}
			southwest: {
				lat: number
				lng: number
			}
		}
	}
	place_id: string
	plus_code?: {
		compound_code: string
		global_code: string
	}
	types: string[]
}

export interface AddressComponent {
	long_name: string
	short_name: string
	types: string[]
}

export interface ReverseGeocodeResponse {
	results: GeocodeResult[]
	status: string
	error_message?: string
}

export interface GeocodeValidationResult {
	isValid: boolean
	isInvalidTerrain: boolean
	terrainType?: string
	formattedAddress?: string
	data: ReverseGeocodeResponse
}

interface ReverseGeocodeParams {
	lat: number
	lng: number
	languageCode?: string
}

const INVALID_TERRAIN_TYPES = [
	'natural_feature', // Características naturales
	'mountain_peak', // Picos de montañas
	'park', // Parques
	'establishment', // Establecimientos genéricos (solo si no hay tipos más específicos)
	'point_of_interest', // Puntos de interés genéricos
	'colloquial_area', // Áreas coloquiales sin dirección específica
]

const VALID_LOCATION_TYPES = [
	'street_address', // Direcciones de calle
	'premise', // Edificios/propiedades
	'route', // Rutas/caminos
	'locality', // Ciudades/localidades
	'sublocality', // Sublocalidades
	'neighborhood', // Barrios
	'administrative_area_level_3', // Áreas administrativas nivel 3
	'administrative_area_level_2', // Municipios/condados
	'administrative_area_level_1', // Estados/provincias
	'postal_code', // Códigos postales
	'subpremise', // Sub-propiedades
	'plus_code', // Plus codes
]

const isInvalidTerrain = (result: GeocodeResult): boolean => {
	// Si tiene algún tipo de ubicación válida, no es terreno inválido
	const hasValidType = result.types.some((type) =>
		VALID_LOCATION_TYPES.includes(type)
	)

	if (hasValidType) {
		return false
	}

	// Si no tiene tipos válidos y tiene tipos inválidos, es terreno inválido
	return result.types.some((type) => INVALID_TERRAIN_TYPES.includes(type))
}

const getTerrainType = (result: GeocodeResult): string | undefined => {
	const invalidType = result.types.find((type) =>
		INVALID_TERRAIN_TYPES.includes(type)
	)
	return invalidType
}

const SDK_SRC = 'maps.googleapis.com/maps/api/js'
// El '?' importa: una vez cargado, Google inyecta scripts internos bajo
// /maps/api/js/<Servicio>, y sin el signo también los daría por bootstrap.
const SDK_SCRIPT_SELECTOR = `script[src*="${SDK_SRC}?"]`
const SDK_CALLBACK = '__enlaceGoogleMapsReady'
const SDK_TIMEOUT_MS = 10_000

let sdkPromise: Promise<void> | undefined

const isSdkReady = () => typeof window.google?.maps === 'object'

/**
 * Carga el bootstrap de Maps JS una sola vez.
 *
 * Esta página monta Mapbox, no `<GoogleMap>`, así que el SDK no viene cargado.
 * Se pide con `callback` —no basta el evento `load` del script: dispara antes
 * de que `google.maps` esté armado y `new Geocoder()` truena.
 *
 * Si otro componente (vue3-google-map) ya inyectó su script, se espera a ese
 * en vez de meter un segundo, que Google castiga con el warning de "included
 * multiple times".
 */
const loadGoogleMapsSdk = (): Promise<void> => {
	if (isSdkReady()) return Promise.resolve()
	if (sdkPromise) return sdkPromise

	sdkPromise = new Promise<void>((resolve, reject) => {
		const fail = () => {
			sdkPromise = undefined
			reject(new Error('No se pudo cargar el SDK de Google Maps'))
		}

		const existing = document.querySelector<HTMLScriptElement>(
			SDK_SCRIPT_SELECTOR
		)

		if (existing) {
			const startedAt = Date.now()
			const poll = window.setInterval(() => {
				if (isSdkReady()) {
					window.clearInterval(poll)
					resolve()
					return
				}
				if (Date.now() - startedAt > SDK_TIMEOUT_MS) {
					window.clearInterval(poll)
					fail()
				}
			}, 150)
			return
		}

		window[SDK_CALLBACK] = () => resolve()

		const script = document.createElement('script')
		script.async = true
		script.src =
			`https://${SDK_SRC}` +
			`?key=${import.meta.env.VITE_GOOGLE_KEY}` +
			`&language=es&callback=${SDK_CALLBACK}`
		script.addEventListener('error', fail)
		document.head.appendChild(script)
	})

	return sdkPromise
}

/**
 * El SDK expone el geocoder de dos formas según quién lo haya cargado: con el
 * bootstrap moderno (el que usa vue3-google-map) hay que pedir la librería;
 * con la carga clásica, `Geocoder` ya cuelga de `google.maps`.
 */
const getGeocoder = async (): Promise<google.maps.Geocoder> => {
	if (typeof window.google?.maps?.importLibrary === 'function') {
		const { Geocoder } = await google.maps.importLibrary('geocoding')
		return new Geocoder()
	}

	return new google.maps.Geocoder()
}

/**
 * Traduce el resultado del SDK a la forma del web service, que es la que
 * consume el resto del código: `location` deja de ser un `LatLng` con métodos
 * y pasa a ser `{ lat, lng }` plano.
 */
const toGeocodeResult = (result: google.maps.GeocoderResult): GeocodeResult => {
	const northeast = result.geometry.viewport.getNorthEast()
	const southwest = result.geometry.viewport.getSouthWest()

	return {
		address_components: result.address_components,
		formatted_address: result.formatted_address,
		geometry: {
			location: {
				lat: result.geometry.location.lat(),
				lng: result.geometry.location.lng(),
			},
			location_type: result.geometry.location_type,
			viewport: {
				northeast: { lat: northeast.lat(), lng: northeast.lng() },
				southwest: { lat: southwest.lat(), lng: southwest.lng() },
			},
		},
		place_id: result.place_id,
		types: result.types,
	}
}

/**
 * Va por el SDK de Maps JS y no por `maps/api/geocode/json` a propósito: el
 * web service REST rechaza de plano las llaves restringidas por referrer
 * ("API keys with referer restrictions cannot be used with this API"), que es
 * justo el tipo de llave que viaja en el bundle en testing y producción. El
 * SDK sí las acepta, validando el dominio contra la lista blanca.
 */
const getReverseGeocode = async (
	params: ReverseGeocodeParams
): Promise<GeocodeValidationResult> => {
	const { lat, lng, languageCode = 'es' } = params

	await loadGoogleMapsSdk()

	const geocoder = await getGeocoder()

	// ZERO_RESULTS llega como rechazo de la promesa, no como lista vacía.
	const response = await geocoder
		.geocode({ location: { lat, lng }, language: languageCode })
		.catch(() => undefined)

	const results = (response?.results ?? []).map(toGeocodeResult)

	let isInvalidTerrainFlag = false
	let terrainType: string | undefined

	if (results.length > 0) {
		isInvalidTerrainFlag = isInvalidTerrain(results[0])
		if (isInvalidTerrainFlag) {
			terrainType = getTerrainType(results[0])
		}
	}

	return {
		isValid: !isInvalidTerrainFlag,
		isInvalidTerrain: isInvalidTerrainFlag,
		terrainType,
		formattedAddress: results[0]?.formatted_address,
		data: {
			results,
			status: results.length > 0 ? 'OK' : 'ZERO_RESULTS',
		},
	}
}

export const useReverseGeocode = (params: ReverseGeocodeParams) => {
	const paramsGeo = reactive({
		lat: params.lat,
		lng: params.lng,
		languageCode: params.languageCode,
	})

	// La key lleva las coordenadas: antes era fija y el caché devolvía siempre
	// el primer punto consultado, así que un clic nuevo se validaba con el
	// terreno del clic anterior.
	const queryKey = computed(() => [
		'reverseGeocode',
		paramsGeo.lat,
		paramsGeo.lng,
		paramsGeo.languageCode,
	])

	// Forma de objeto: es la única que acepta una key reactiva en vue-query v4.
	const { data, error, refetch, isFetching } = useQuery({
		queryKey,
		queryFn: () => getReverseGeocode(paramsGeo),
		retry: false,
		enabled: false,
	})

	return {
		geocodeData: data,
		error,
		isFetching,
		refetch,
		getGeocode: async () => {
			return await refetch()
		},
		paramsGeo,
	}
}
